import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { FR_TOOLS, FR_CATEGORIES } from "@/lib/fr-tools-catalog";
import { FR_PDF_TOOLS } from "@/lib/fr-pdf-catalog";
import { Sparkles, FileText, ArrowRight, ShieldCheck, Zap, Disc3 } from "lucide-react";

export const Route = createFileRoute("/fr/")({
  head: () => ({
    meta: [
      { title: "Outils Gratuits pour la Classe, Tirages au Sort et PDF — Picamini.online" },
      {
        name: "description",
        content:
          "Tirages au sort de prénoms, roues de la fortune interactives, générateurs d'équipes et plus de 26 outils PDF gratuits. 100% privé, sans inscription et directement dans votre navigateur.",
      },
      {
        name: "keywords",
        content:
          "tirage au sort prenom, roue de la fortune en ligne, roue des prenoms, fusionner pdf gratuit, generateur d equipes, outils enseignants, picamini france",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Outils Gratuits pour la Classe et PDF — Picamini.online" },
      {
        property: "og:description",
        content: "Tirages au sort rapides, roues interactives et 26+ outils PDF 100% privés dans votre navigateur.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/fr/" },
      { property: "og:locale", content: "fr_FR" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/fr/" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/" },
    ],
  }),
  component: FrHomePage,
});

function FrHomePage() {
  const featuredTools = FR_TOOLS.filter((t) => t.featured);

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="fr" />

      <main className="flex-1">
        {/* HERO SECTION */}
        <section className="bg-gradient-to-b from-primary/5 via-background to-background py-16 sm:py-20 border-b border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 text-center space-y-5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary">
              <Sparkles className="size-4" /> 37 Outils Interactifs + 26 Utilitaires PDF Gratuits
            </span>

            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl text-foreground max-w-4xl mx-auto leading-[1.15]">
              Outils Pédagogiques, Tirages au Sort et Éditeur PDF <span className="text-primary">100% Gratuits</span>
            </h1>

            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Pour enseignants, étudiants, formateurs et équipes. Réalisez des tirages au sort de prénoms, faites tourner des roues interactives et éditez vos PDF directement dans votre navigateur sans aucune inscription.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <a
                href="#outils-interactifs"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-extrabold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105"
              >
                <Disc3 className="size-5" /> Explorer les 37 Outils
              </a>
              <Link
                to="/fr/pdf-tools"
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-extrabold text-foreground shadow-xs hover:bg-secondary transition-all hover:scale-105"
              >
                <FileText className="size-5 text-primary" /> Suite PDF Toolkit Pro
              </Link>
            </div>
          </div>
        </section>

        {/* TRUST VALUE PROPOSITION */}
        <section className="py-10 bg-card border-b border-border/60">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 sm:grid-cols-3 text-center">
              <div className="flex flex-col items-center p-4">
                <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary mb-3">
                  <ShieldCheck className="size-6" />
                </div>
                <h3 className="font-bold text-foreground text-base">100% Confidentiel & Sécurisé</h3>
                <p className="text-xs text-muted-foreground mt-1">Vos données et fichiers ne quittent jamais votre navigateur.</p>
              </div>

              <div className="flex flex-col items-center p-4">
                <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary mb-3">
                  <Zap className="size-6" />
                </div>
                <h3 className="font-bold text-foreground text-base">Accès Instantané Sans Compte</h3>
                <p className="text-xs text-muted-foreground mt-1">Aucune inscription ni adresse e-mail requise.</p>
              </div>

              <div className="flex flex-col items-center p-4">
                <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary mb-3">
                  <Sparkles className="size-6" />
                </div>
                <h3 className="font-bold text-foreground text-base">Adapté aux Écrans de Classe</h3>
                <p className="text-xs text-muted-foreground mt-1">Graphismes fluides et visuels optimisés pour TBI et vidéoprojecteur.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED TOOLS */}
        <section id="outils-interactifs" className="py-16 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-extrabold text-foreground">Outils Interactifs les Plus Populaires</h2>
            <p className="text-muted-foreground">Sélectionnés pour leur simplicité, leur équité et leur impact visuel.</p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featuredTools.map((tool) => (
              <Link
                key={tool.slug}
                to="/fr/$slug"
                params={{ slug: tool.slug }}
                className="group relative flex flex-col justify-between rounded-2xl border border-border/80 bg-card p-6 shadow-xs hover:shadow-md hover:border-primary/50 transition-all"
              >
                <div className="space-y-3">
                  <div className="inline-flex rounded-xl bg-primary/10 p-3 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Disc3 className="size-6" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">{tool.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{tool.tagline}</p>
                </div>
                <div className="mt-6 flex items-center gap-1 text-xs font-extrabold text-primary">
                  Utiliser l'outil <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* PDF TOOLS SECTION */}
        <section className="py-16 bg-card border-y border-border/60">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-primary">Suite PDF Professionnelle</span>
                <h2 className="text-3xl font-extrabold text-foreground mt-1">26 Outils PDF Gratuits Traités en Local</h2>
              </div>
              <Link
                to="/fr/pdf-tools"
                className="inline-flex items-center gap-1.5 text-sm font-extrabold text-primary hover:underline"
              >
                Voir tous les 26 outils PDF <ArrowRight className="size-4" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {FR_PDF_TOOLS.slice(0, 8).map((tool) => (
                <Link
                  key={tool.slug}
                  to="/fr/pdf-tools/$slug"
                  params={{ slug: tool.slug }}
                  className="rounded-xl border border-border/70 bg-background p-4 hover:border-primary/50 hover:shadow-xs transition-all space-y-2"
                >
                  <h3 className="font-bold text-foreground text-base hover:text-primary">{tool.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-2">{tool.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ALL CATEGORIES */}
        <section className="py-16 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-extrabold text-foreground">Catalogue de Tous les 37 Outils par Catégorie</h2>
            <p className="text-muted-foreground">Trouvez immédiatement l'outil adapté à votre situation.</p>
          </div>

          <div className="space-y-10">
            {FR_CATEGORIES.map((cat) => {
              const catTools = FR_TOOLS.filter((t) => t.category === cat);
              return (
                <div key={cat} className="space-y-4">
                  <h3 className="text-xl font-bold text-foreground border-b border-border pb-2 flex items-center gap-2">
                    <span className="size-2.5 rounded-full bg-primary inline-block" /> {cat}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {catTools.map((t) => (
                      <Link
                        key={t.slug}
                        to="/fr/$slug"
                        params={{ slug: t.slug }}
                        className="rounded-xl border border-border/80 bg-card p-4 hover:border-primary/50 hover:shadow-xs transition-all flex items-start justify-between gap-3"
                      >
                        <div>
                          <h4 className="font-bold text-foreground text-sm hover:text-primary">{t.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.tagline}</p>
                        </div>
                        <ArrowRight className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <SiteFooter lang="fr" />
    </div>
  );
}
